import { useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Heading from './Heading'
import Modal from './Modal'
import Avatar from './Avatar'
import Button from './Button'
import Check from '../icons/Check'
import ArrowUpload from '../icons/ArrowUpload'
import ServiceEndpoints from '../modules/serviceEndpoints'
import useRequest from '../hooks/useRequest'
import useAuthenticatedRequest from '../hooks/useAuthenticatedRequest'
import getAvatarUrl from '../modules/getAvatarUrl'

function AvatarUploadModal({
  onClose,
  onSuccess,
  title,
  confirmText,
  type,
  id,
}) {
  const inputRef = useRef(null)

  const [localFile, setLocalFile] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isValidFile, setIsValidFile] = useState(false)
  const [localUrl, setLocalUrl] = useState(null)
  const avatarUrl = getAvatarUrl(type, id, 120)

  const isUploadDisabled = useMemo(() => isUploading || !isValidFile, [isUploading, isValidFile])
  const [errorMessage, setErrorMessage] = useState(null)

  const authenticatedRequest = useAuthenticatedRequest()
  const defaultRequest = useRequest()
  const maximumFileSize = 5242880
  const timeoutLimit = 60000
  const allowedMimeTypes = [
    'image/gif',
    'image/jpe',
    'image/jpg',
    'image/jpeg',
    'image/png',
    'image/webp',
  ]

  const handleFileSelect = (file) => {
    setErrorMessage(null)
    setLocalUrl(null)
    setIsValidFile(false)

    if (file == null) {
      return
    }

    if (file.size >= maximumFileSize) {
      setErrorMessage('The selected file is too large.')
      return
    }

    if (!allowedMimeTypes.includes(file.type)) {
      setErrorMessage('The file is not of type png, jpg, jpeg, gif or webp.')
      return
    }

    setLocalUrl(URL.createObjectURL(file))
    setIsValidFile(true)
    setLocalFile(file)
  }

  const handleFileSelectEvent = (event) => {
    event.stopPropagation()
    event.preventDefault()
    handleFileSelect((event && event.target && event.target.files && event.target.files[0]) || null)
  }

  const handleFileDropEvent = (event) => {
    event.stopPropagation()
    event.preventDefault()
    handleFileSelect((event && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]) || null)
  }

  const retrieveAvatarModified = async () => {
    try {
      const avatarHeaders = await fetch(avatarUrl, {
        method: 'get',
      })

      return avatarHeaders.headers.get('last-modified')
    } catch (e) {
      return ''
    }
  }

  const handleFileUpload = async () => {
    setIsUploading(true)
    try {
      if (!type || !id) {
        setErrorMessage('Something expected happened, please try again')
        return
      }

      const signedUriRequest = await authenticatedRequest(`${ServiceEndpoints.files}/avatars/${type}/${id}`, {
        method: 'post',
        data: {
          contentType: localFile.type,
          contentLength: localFile.size,
          originalFileName: localFile.name,
        },
      })

      const timestampBeforeUpload = Date.now()
      const modifiedBeforeUpload = await retrieveAvatarModified()

      const {
        httpMethod, headers, url,
      } = signedUriRequest.data.authorizedRequest
      const flatHeaders = Object.fromEntries(Object.entries(headers).map((entry) => ([entry[0], entry[1][0]])))

      await defaultRequest(url, {
        method: httpMethod,
        headers: flatHeaders,
        data: localFile,
      })

      let modifiedAfterUpload

      do {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => { setTimeout(resolve, 500) })
        // eslint-disable-next-line no-await-in-loop
        modifiedAfterUpload = await retrieveAvatarModified()
      } while (modifiedAfterUpload === modifiedBeforeUpload && Date.now() - timestampBeforeUpload < timeoutLimit)

      if (Date.now() - timestampBeforeUpload > timeoutLimit) {
        setIsUploading(false)
        setErrorMessage('Remote processing of the avatar is taking longer than expected, '
          + 'it may take a while before the new avatar becomes visible. You can close this window.')
        return
      }

      setIsUploading(false)
      onSuccess()
    } catch (e) {
      // Todo error handling
      setErrorMessage('Something unexpected happened, please try again')
      // no-op
    }
  }

  return (
    <Modal className="w-10/12 xl:w-[500px] h-auto" onClose={() => onClose()}>
      <div className="flex flex-col w-full h-full relative content-between">
        <Heading fontSize={Heading.size.H4}>{title}</Heading>
        <div className="text-gray-600 text-sm mt-2">
          Please upload png, jpg, jpeg, gif or webp and make sure your file size is max 5MB.
        </div>
        <div
          className="flex flex-grow flex-col justify-center items-center mt-5 lg:mt-10"
          onDrop={handleFileDropEvent}
          onDragOver={(event) => {
            event.stopPropagation()
            event.preventDefault()
          }}
        >
          <input
            type="file"
            ref={inputRef}
            style={{ display: 'none' }}
            onChange={handleFileSelectEvent}
          />
          <Avatar className="w-20 h-20 lg:w-40 lg:h-40" id="placeholder" image={localUrl} />
          {errorMessage && (
            <div className="text-red-500 text-xs text-center mt-2">
              {errorMessage}
            </div>
          )}
          <Button
            small
            leadingAddon={<ArrowUpload />}
            className="max-w-[10rem] mt-3 flex-grow-0"
            onClick={() => inputRef.current?.click()}
          >
            Browse
          </Button>
        </div>
        <Button
          small
          className="self-end mt-5 lg:mt-10"
          primary
          disabled={isUploadDisabled}
          leadingAddon={<Check />}
          onClick={() => handleFileUpload()}
        >
          {isUploading ? 'Uploading...' : confirmText}
        </Button>
      </div>
    </Modal>
  )
}

AvatarUploadModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  title: PropTypes.string,
  confirmText: PropTypes.string,
  type: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
}

AvatarUploadModal.defaultProps = {
  title: '',
  confirmText: '',
}

export default AvatarUploadModal
